import { forwardRef, useEffect, useRef, useState } from 'react';
import { Box, Grid, useTheme, useMediaQuery, Card, CardContent, Tooltip } from '@mui/material';
import { EventList } from '../Events';
import CloseIcon from '@mui/icons-material/Close';
import { Event } from '@sm-dashboard/types';
import { date, object, string } from 'yup';
import { FieldErrors, useForm } from 'react-hook-form';
import { useAppContext, AutocompleteType } from '@/context/context';
import { yupResolver } from '@hookform/resolvers/yup';
import { useFetch } from '@/hooks/useFetch';
import FormProvider, { InputDate, InputField, InputTime } from '@sm-dashboard/form-provider';
import { Autocomplete, GoogleMap } from '@react-google-maps/api';
import { addEventUrl, deleteEventUrl, updateEventUrl } from '@/constants';
import { EditorComponent } from '@sm-dashboard/form-provider';
import { theme } from '@/theme/theme';
import DeleteDialog from './DeleteDialog';
import instanceAxios from '@/util/axios';
import { getShaliachById, getShaliachIdFromValue } from '@/util/commonFunction';
import AddressMapPicker, { AddressPickerData } from '../../Map/AddressMapPicker/AddressMapPicker';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { showMissingFieldsSnackbar } from '@/components/common/MissingFieldsMessage';
import { EventImageUpload } from './EventImageUpload';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import {
  Autocomplete as ShaliachAutocomplete,
  Button,
  Icon,
  IconButton,
  Input,
  RadioGroup,
  Switch,
  Typography,
  Dialog
} from '@sm-dashboard/ui-components';
import { AiFillFromAd, AiFillConfirmation, AiFillIntro, useAiFillFromAd } from './AiFillFromAd';
import { timeToDate } from './timeUtils';

function escapeHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function htmlToPlainText(value: string | undefined) {
  if (!value) return '';
  return value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function plainTextToHtml(value: string) {
  if (!value.trim()) return '';
  return value
    .split('\n')
    .map(line => `<p>${escapeHtml(line) || '<br>'}</p>`)
    .join('');
}

type PlacesAddressInputProps = {
  placeholder: string;
  iconUrl?: string;
  setLocationPicker: React.Dispatch<React.SetStateAction<AddressPickerData | undefined>>;
};

const PlacesAddressInput = forwardRef<HTMLInputElement, PlacesAddressInputProps>(function PlacesAddressInput(
  { placeholder, iconUrl, setLocationPicker },
  ref
) {
  const theme = useTheme();
  const operationIconSize = theme.spacing(3.5);

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 1,
        bgcolor: 'background.default',
        borderRadius: 0
      }}
      // Enter here selects a Google-Places suggestion; without this it also submits the whole form.
      onKeyDown={event => event.key === 'Enter' && event.preventDefault()}
    >
      <Input
        label="כתובת"
        fullWidth
        placeholder={placeholder}
        inputRef={ref}
        startIcon={
          iconUrl ? (
            <Box
              component="img"
              src={`/icons${iconUrl}`}
              alt=""
              sx={{
                width: operationIconSize,
                height: operationIconSize,
                objectFit: 'contain',
                display: 'block',
                flexShrink: 0
              }}
            />
          ) : undefined
        }
        endIcon={<AddressMapPicker setLocationPicker={setLocationPicker} icon={iconUrl} />}
      />
    </Box>
  );
});

const isEmptyFormValue = (value: unknown) => {
  if (value == null) return true;
  if (value instanceof Date) return Number.isNaN(value.getTime());
  return String(value).trim() === '';
};

export type FormValuesProps = {
  title: string;
  description: string;
  date: Date | string;
  fromDate: Date | string;
  toDate: Date | string;
  time: Date | string;
  fromTime: Date | string;
  toTime: Date | string;
  phone: string;
  link: string;
  thumbnail?: string;
  image?: string;
};

interface EventFormDraft {
  values: FormValuesProps;
  content?: string;
  locationPicker?: AddressPickerData;
  placeholder: string;
  aiFillApplied: boolean;
  aiConfirmed: boolean;
}

// In-memory drafts that survive the unmount/remount caused by the desktop↔mobile
// layout switch when the window is resized across the md breakpoint.
const formDraftCache = new Map<string, EventFormDraft>();

interface EventFormProps {
  event: EventList;
  index: number;
  deleteEventFromList: (index: number) => void;
  expanded: number | false;
  handleAccordionsChange: (panelIndex: number) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  isNewEvent?: boolean;
  onEventAdded?: (index: number, updatedEventData: any) => void;
  onEventClick?: (index: number) => void;
  onFormClose?: (index: number) => void;
}

const EventForm = ({
  event,
  index,
  deleteEventFromList,
  expanded,
  handleAccordionsChange,
  isNewEvent,
  onEventAdded,
  onEventClick,
  onFormClose
}: EventFormProps) => {
  const { data, config } = event;
  const { data: dataAdd, error: errorAdd, loading: loadingAdd, fetchData: fetchAddDate } = useFetch();
  const { data: dataUpdate, error: errorUpdate, loading: loadingUpdate, fetchData: fetchUpdateDate } = useFetch();
  const { data: dataDelete, error: errorDelete, loading: loadingDelete, fetchData: fetchDeleteDate } = useFetch();
  const { setSnackbar, isGoogleMapsApiLoaded, userPermission, allShluchim, shaliach } = useAppContext();
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [placeholder, setPlaceholder] = useState(
    data?.address || (data?.Operation?.id === 215 ? 'כתובת דוכן התפילין' : 'מיקום האירוע')
  );

  const [id, setId] = useState(data.id);
  const [locationPicker, setLocationPicker] = useState<AddressPickerData | undefined>(undefined);
  const [content, setContent] = useState<string | undefined>(
    data?.content || config?.defaultText || '<p>טקסט חופשי ותמונה</p>'
  );
  const [showRichEditor, setShowRichEditor] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [isActive, setIsActive] = useState(data.isActive || false);
  const [openDialog, setOpenDialog] = useState(false);
  const [malashSale, setMalashSale] = useState(
    config?.Operation && config?.Operation.id === 203 ? 'אספקה ממלש' : 'דוכן תפילין מאויש'
  );
  const [isSaving, setIsSaving] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedShaliach, setSelectedShaliach] = useState<AutocompleteType | null>(null);

  useEffect(() => {
    if (!userPermission?.isAdmin || !isNewEvent || shaliach?.id == null) {
      return;
    }

    const selfOption = getShaliachById(String(shaliach.id), allShluchim);

    if (!selfOption) {
      return;
    }

    setSelectedShaliach(prev => prev ?? selfOption);
  }, [userPermission?.isAdmin, isNewEvent, shaliach?.id, allShluchim]);
  // New events open on the AI intro screen; extraction success or "fill manually" moves to the form.
  const [showAiIntro, setShowAiIntro] = useState(Boolean(isNewEvent));
  const showAiIntroRef = useRef(showAiIntro);
  showAiIntroRef.current = showAiIntro;

  const draftKey = data.id ? `event-${data.id}` : `new-${config?.operationId}`;
  const skipDraftSaveRef = useRef(false);

  const discardDraft = () => {
    skipDraftSaveRef.current = true;
    formDraftCache.delete(draftKey);
  };
  const [isFormSaved, setIsFormSaved] = useState(false);

  const [showSaveSuccessDialog, setShowSaveSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const schema = object({
    title: string(),
    description: string(),
    date: string(),
    fromDate: string(),
    toDate: string(),
    time: config?.isTime ? date() : string(),
    fromTime: config?.isRangeTime ? date() : string(),
    toTime: config?.isRangeTime ? date() : string(),
    phone: string(),
    link: string(),
    thumbnail: string(),
    image: string()
  }).required();

  const methods = useForm<FormValuesProps, Event>({
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: {
      title: data.title || '',
      description: data.description || '',
      date: data?.date ? new Date(data.date) : config?.isDate ? new Date() : '',
      fromDate: data?.fromDate ? new Date(data.fromDate) : config?.isRangeDate ? new Date() : '',
      toDate: data?.toDate ? new Date(data.toDate) : config?.isRangeDate ? new Date() : '',
      time: data?.time ? timeToDate(data.time) : config?.isTime ? new Date() : '',
      fromTime: data?.fromTime ? timeToDate(data.fromTime) : config?.isRangeTime ? new Date() : '',
      toTime: data?.toTime ? timeToDate(data.toTime) : config?.isRangeTime ? new Date() : '',
      phone: data.phone || '',
      link: data.link || '',
      thumbnail: data.thumbnail || '',
      image: data.image || ''
    }
  });

  const { handleSubmit, clearErrors, getValues } = methods;

  const { aiFillApplied, setAiFillApplied, aiConfirmed, setAiConfirmed, isExtracting, handleImageUploaded, handleFreeText } =
    useAiFillFromAd({
      methods,
      config,
      content,
      setContent,
      locationPicker,
      setLocationPicker,
      setPlaceholder
    });

  const latestUiStateRef = useRef({ content, locationPicker, placeholder, aiFillApplied, aiConfirmed });
  latestUiStateRef.current = { content, locationPicker, placeholder, aiFillApplied, aiConfirmed };

  const fieldLabelByName: Record<string, string> = {
    title: data?.Operation?.id === 215 ? 'שם בית העסק' : 'שם המוקד (לשימוש אישי)',
    description: data?.Operation?.id === 215 ? 'מיקום העמדה או הקדשה' : 'תיאור האירוע',
    date: 'תאריך',
    fromDate: 'מתאריך',
    toDate: 'עד תאריך',
    time: 'שעה',
    fromTime: 'משעה',
    toTime: 'עד שעה',
    phone: 'טלפון'
  };

  const getMissingFields = (dataForm: FormValuesProps) => {
    const missing: string[] = [];
    const requiresShaliach = Boolean(userPermission?.isAdmin && isNewEvent && !id);
    const selectedShaliachId = selectedShaliach
      ? Number(getShaliachIdFromValue(selectedShaliach.value))
      : NaN;

    if (requiresShaliach && Number.isNaN(selectedShaliachId)) {
      missing.push('שליח וסניף');
    }

    if (config?.isAddress && !id) {
      const location = locationPicker?.location || autocomplete?.getPlace()?.geometry?.location;
      if (!location) {
        missing.push('כתובת');
      }
    }

    if (config?.isTitle && isEmptyFormValue(dataForm.title)) {
      missing.push(fieldLabelByName.title);
    }
    if (config?.isDescription && isEmptyFormValue(dataForm.description)) {
      missing.push(fieldLabelByName.description);
    }
    if (config?.isDate && isEmptyFormValue(dataForm.date)) {
      missing.push(fieldLabelByName.date);
    }
    if (config?.isRangeDate && isEmptyFormValue(dataForm.fromDate)) {
      missing.push(fieldLabelByName.fromDate);
    }
    if (config?.isRangeDate && isEmptyFormValue(dataForm.toDate)) {
      missing.push(fieldLabelByName.toDate);
    }
    if (config?.isTime && isEmptyFormValue(dataForm.time)) {
      missing.push(fieldLabelByName.time);
    }
    if (config?.isRangeTime && isEmptyFormValue(dataForm.fromTime)) {
      missing.push(fieldLabelByName.fromTime);
    }
    if (config?.isRangeTime && isEmptyFormValue(dataForm.toTime)) {
      missing.push(fieldLabelByName.toTime);
    }
    if (config?.isPhone && isEmptyFormValue(dataForm.phone)) {
      missing.push(fieldLabelByName.phone);
    }

    return missing;
  };

  const collectMissingFields = (errors?: FieldErrors<FormValuesProps>) => {
    const fromSchema = errors
      ? Object.keys(errors)
          .map(name => fieldLabelByName[name])
          .filter((label): label is string => Boolean(label))
      : [];
    return [...new Set([...fromSchema, ...getMissingFields(getValues())])];
  };

  const onInvalid = (errors: FieldErrors<FormValuesProps>) => {
    showMissingFieldsSnackbar(setSnackbar, collectMissingFields(errors));
    clearErrors();
  };

  const onSubmit = (dataForm: FormValuesProps) => {
    if (isSaving || formSubmitted) {
      return;
    }

    // The disabled save button is not enough — an implicit submit (e.g. Enter in a field)
    // must not bypass the AI-fill confirmation.
    if (aiFillApplied && !aiConfirmed) {
      setSnackbar({
        open: true,
        severity: 'warning',
        message: 'לפני השמירה יש לבדוק את הפרטים שמולאו מהמודעה ולסמן את תיבת האישור'
      });
      return;
    }

    const missingFields = getMissingFields(dataForm);
    if (missingFields.length) {
      showMissingFieldsSnackbar(setSnackbar, missingFields);
      return;
    }

    const requiresShaliach = Boolean(userPermission?.isAdmin && isNewEvent && !id);
    const selectedShaliachId = selectedShaliach
      ? Number(getShaliachIdFromValue(selectedShaliach.value))
      : NaN;

    setIsSaving(true);
    setIsFormSaved(true);
    const location = locationPicker?.location || autocomplete?.getPlace()?.geometry?.location;

    const baseObj = {
      ...dataForm,
      time: dataForm?.time ? (dataForm.time as Date).toTimeString().split(' ')[0] : '',
      fromTime: dataForm?.fromTime ? (dataForm.fromTime as Date).toTimeString().split(' ')[0] : '',
      toTime: dataForm?.toTime ? (dataForm.toTime as Date).toTimeString().split(' ')[0] : '',
      content: content === '<p>טקסט חופשי ותמונה</p>' || content === config?.defaultText ? '' : content,
      operationId: config?.operationId,
      isSaleMalash: malashSale
    };

    if (!id) {
      fetchAddDate({
        method: 'POST',
        url: addEventUrl,
        data: {
          ...baseObj,
          address: `${location
            ? locationPicker?.address || `${autocomplete?.getPlace().name} ${autocomplete?.getPlace().vicinity}`
            : ''
            }`,
          position: { lat: location ? location?.lat() : 0, lng: location ? location?.lng() : 0 },
          isActive: true,
          ...(requiresShaliach ? { shaliachId: selectedShaliachId } : {})
        }
      });
      setIsActive(true);
      setFormSubmitted(true);
    } else {
      let updateObj = {};
      if (location) {
        updateObj = {
          ...baseObj,
          id,
          address: `${location
            ? locationPicker?.address || `${autocomplete?.getPlace().name} ${autocomplete?.getPlace().vicinity}`
            : ''
            }`,
          position: { lat: location ? location?.lat() : 0, lng: location ? location?.lng() : 0 }
        };
      } else {
        updateObj = { ...baseObj, id };
      }
      fetchUpdateDate({
        method: 'POST',
        url: updateEventUrl,
        data: updateObj
      });
    }
  };

  const onLoad = (autocompleteLoad: google.maps.places.Autocomplete) => {
    setAutocomplete(autocompleteLoad);
  };

  const onPlaceChanged = () => {
    if (!autocomplete) return;

    const place = autocomplete.getPlace();
    if (!place || !place.geometry || !place.geometry.location) return;

    const location = place.geometry.location;
    const address = place.formatted_address || `${place.name || ''} ${place.vicinity || ''}`.trim();

    setLocationPicker({
      location,
      address
    } as AddressPickerData);

    setPlaceholder(address);
  };

  const handleDeleteEvent = async () => {
    if (id) {
      try {
        await fetchDeleteDate({
          method: 'DELETE',
          url: deleteEventUrl,
          data: {
            id
          }
        });
        if (dataDelete && !errorDelete) {
          deleteEventFromList(index);
          setSnackbar({ severity: 'success', message: 'אירוע נמחק בהצלחה', open: true });
        }
      } catch (error) {
        setSnackbar({ severity: 'error', message: 'שגיאה במחיקת האירוע', open: true });
      }
    } else {
      deleteEventFromList(index);
    }
  };

  const handleSwitch = () => {
    if (id) {
      instanceAxios
        .post(updateEventUrl, { id, isActive: !isActive, operationId: config?.operationId })
        .then(() => {
          setSnackbar({ open: true, severity: 'success', message: `האירוע הוגדר ${isActive ? 'כלא פעיל' : 'כפעיל'}` });
        })
        .catch(err => console.log(err));
    }
    setIsActive(!isActive);
  };

  const handleMalashChange = (value: string) => {
    setMalashSale(value);
  };

  useEffect(() => {
    if (dataDelete && !errorDelete) {
      discardDraft();
      deleteEventFromList(index);
      setSnackbar({ severity: 'success', message: 'אירוע נמחק בהצלחה', open: true });
    } else if (errorDelete) {
      setSnackbar({ severity: 'error', message: 'שגיאה במחיקת האירוע', open: true });
    }
  }, [dataDelete, errorDelete]);

  useEffect(() => {
    methods.setValue('title', data.title || '');
    methods.setValue('description', data.description || '');
    methods.setValue('date', data?.date ? new Date(data.date) : config?.isDate ? new Date() : '');
    methods.setValue('fromDate', data?.fromDate ? new Date(data.fromDate) : config?.isRangeDate ? new Date() : '');
    methods.setValue('toDate', data?.toDate ? new Date(data.toDate) : config?.isRangeDate ? new Date() : '');
    methods.setValue('time', data?.time ? timeToDate(data.time) : config?.isTime ? new Date() : '');
    methods.setValue('fromTime', data?.fromTime ? timeToDate(data.fromTime) : config?.isRangeTime ? new Date() : '');
    methods.setValue('toTime', data?.toTime ? timeToDate(data.toTime) : config?.isRangeTime ? new Date() : '');
    methods.setValue('phone', data.phone || '');
    methods.setValue('link', data.link || '');
    methods.setValue('thumbnail', data.thumbnail || '');
    methods.setValue('image', data.image || '');
    setContent(data?.content || config?.defaultText || '<p>טקסט חופשי ותמונה</p>');
    setShowRichEditor(false);
    data?.address && setPlaceholder(data.address);
    setIsActive(data.isActive || false);
    setId(data.id);
  }, [data]);

  // Restore a draft saved before a layout-switch remount (window resized across the md breakpoint).
  useEffect(() => {
    const draft = formDraftCache.get(draftKey);
    if (!draft) return;
    formDraftCache.delete(draftKey);
    // A draft means the user was already past the intro when the layout switched.
    setShowAiIntro(false);
    methods.reset(draft.values);
    setContent(draft.content);
    if (draft.locationPicker) setLocationPicker(draft.locationPicker);
    setPlaceholder(draft.placeholder);
    setAiFillApplied(draft.aiFillApplied);
    setAiConfirmed(draft.aiConfirmed);
    // Sync the ref immediately: under StrictMode the save-cleanup runs before the
    // state updates above re-render, and would otherwise re-save a draft holding
    // the stale pre-restore UI state (editor content, address, AI flags).
    latestUiStateRef.current = {
      content: draft.content,
      locationPicker: draft.locationPicker ?? latestUiStateRef.current.locationPicker,
      placeholder: draft.placeholder,
      aiFillApplied: draft.aiFillApplied,
      aiConfirmed: draft.aiConfirmed
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save a draft when unmounting mid-edit, unless the form was explicitly closed/saved/deleted.
  useEffect(() => {
    return () => {
      // Nothing worth restoring while still on the intro screen — and a saved draft would skip it.
      if (skipDraftSaveRef.current || showAiIntroRef.current) return;
      formDraftCache.set(draftKey, { values: methods.getValues(), ...latestUiStateRef.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (dataUpdate) {
      discardDraft();
      setSuccessMessage('האירוע עודכן בהצלחה');
      setShowSaveSuccessDialog(true);
      setIsSaving(false);
    } else if (errorUpdate) {
      setSnackbar({ open: true, message: 'שגיאה בשמירת האירוע', severity: 'error' });
      setIsSaving(false);
    }
  }, [dataUpdate, errorUpdate]);

  useEffect(() => {
    if (dataAdd) {
      discardDraft();
      setSuccessMessage('האירוע נוסף בהצלחה');

      if (dataAdd.data) {
        onEventAdded && onEventAdded(index, dataAdd.data);
      }

      setShowSaveSuccessDialog(true);
      setIsSaving(false);
    } else if (errorAdd) {
      setSnackbar({ open: true, message: 'שגיאה בהוספת האירוע', severity: 'error' });
      setIsSaving(false);
    }
  }, [dataAdd, errorAdd]);

  const handleSuccessDialogClose = () => {
    setShowSaveSuccessDialog(false);
    window.location.reload();
  };

  const handleAccordionClick = () => {
    if (isNewEvent && onEventClick) {
      onEventClick(index);
    }
  };

  const formFields = () => {
    return (
      <FormProvider onSubmit={handleSubmit(onSubmit, onInvalid)} methods={methods}>
        {isSmallScreen && (
          <Grid item xs={12} margin={1}>
            <Typography variant="body">{data.title}</Typography>
          </Grid>
        )}
        {config?.isSaleMalash && (
          <Grid item xs={12}>
            <RadioGroup
              label="צורת אספקה"
              row
              size="small"
              name="controlled-radio-buttons-group"
              value={malashSale}
              options={[
                {
                  value: config.Operation?.id === 203 ? 'אספקה ממלש' : 'דוכן תפילין מאויש',
                  label: config.Operation?.id === 203 ? 'אספקה ממלש' : 'דוכן תפילין מאויש'
                },
                {
                  value: config.Operation?.id === 203 ? 'מכירה עצמאית' : 'עמדת תפילין לא מאויש',
                  label: config.Operation?.id === 203 ? 'מכירה עצמאית' : 'עמדת תפילין (לא מאוישת)'
                }
              ]}
              onChange={handleMalashChange}
            />
          </Grid>
        )}
        <Grid container spacing={2} sx={{ paddingBottom: { xs: 6, md: 0 }, width: '100%', minWidth: 0, maxWidth: '100%' }}>
          {userPermission?.isAdmin && isNewEvent && (
            <Grid item xs={12}>
              <ShaliachAutocomplete<AutocompleteType>
                appearance="plain"
                blurOnSelect
                options={allShluchim}
                value={selectedShaliach}
                onChange={(_, value) => {
                  setSelectedShaliach(value);
                }}
                label="שליח וסניף *"
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
              />
            </Grid>
          )}
          {!isNewEvent && !aiFillApplied && (
            <Grid item xs={12} sx={{ minWidth: 0, maxWidth: '100%' }}>
              <AiFillFromAd isExtracting={isExtracting} onUploaded={handleImageUploaded} onFreeText={handleFreeText} />
            </Grid>
          )}
          {!isNewEvent && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: 'grey.500'
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body">{isActive ? 'מוקד פעיל' : 'מוקד כבוי'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isActive
                      ? 'מוקד פעיל מוצג לציבור באתר ובמפה'
                      : 'מוקד כבוי אינו מוצג לציבור באתר ובמפה'}
                  </Typography>
                </Box>
                <Switch
                  checked={isActive}
                  disabled={loadingUpdate || loadingAdd || loadingDelete}
                  onChange={handleSwitch}
                />
              </Box>
            </Grid>
          )}
          {config?.isAddress && (
            <Grid item xs={12}>
              <Box sx={{ width: '100%', position: 'relative' }}>
                <style>
                  {`
                    .event-map-container {
                      overflow: visible !important;
                    }

                    .event-map-container .gm-style {
                      position: absolute !important;
                      width: 1px !important;
                      height: 1px !important;
                      opacity: 0 !important;
                      pointer-events: none !important;
                      overflow: hidden !important;
                      z-index: 0 !important;
                    }

                    .pac-container {
                      z-index: 1301 !important;
                    }
                  `}
                </style>
                <GoogleMap
                  mapContainerClassName="event-map-container"
                  mapContainerStyle={{ width: '100%', height: 'auto', minHeight: 0 }}
                  options={{
                    disableDefaultUI: true,
                    draggable: false,
                    scrollwheel: false,
                    disableDoubleClickZoom: true,
                    keyboardShortcuts: false,
                    backgroundColor: theme.palette.common.white
                  }}
                >
                  <Autocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
                    <PlacesAddressInput
                      placeholder={locationPicker?.address || placeholder}
                      iconUrl={data.Operation?.iconUrl}
                      setLocationPicker={setLocationPicker}
                    />
                  </Autocomplete>
                </GoogleMap>
              </Box>
            </Grid>
          )}
          {config?.isTitle && (
            <Grid item xs={12}>
              <InputField
                name="title"
                label={`${data?.Operation?.id === 215 ? 'שם בית העסק' : 'שם המוקד (לשימוש אישי)'}`}
              />
            </Grid>
          )}
          {config?.isDescription && (
            <Grid item xs={config?.isLink ? 6 : 12}>
              <InputField
                name="description"
                label={`${data?.Operation?.id === 215 ? 'מיקום העמדה או הקדשה' : 'תיאור האירוע'}`}
              />
            </Grid>
          )}
          {config?.isLink && (
            <Grid item xs={config?.isDescription ? 6 : 12}>
              <InputField name="link" label="קישור" />
            </Grid>
          )}
          {config?.isDate && (
            <Grid item xs={12}>
              <InputDate name="date" label="תאריך" />
            </Grid>
          )}
          {config?.isRangeDate && (
            <>
              <Grid item xs={12}>
                <InputDate name="fromDate" label="מתאריך" />
              </Grid>
              <Grid item xs={12}>
                <InputDate name="toDate" label="עד תאריך" />
              </Grid>
            </>
          )}
          {config?.isTime && (
            <Grid item xs={12}>
              <InputTime name="time" label="שעה" />
            </Grid>
          )}
          {config?.isRangeTime && (
            <>
              <Grid item xs={12}>
                <InputTime name="fromTime" label="משעה" />
              </Grid>
              <Grid item xs={12}>
                <InputTime name="toTime" label="עד שעה" />
              </Grid>
            </>
          )}
          {config?.isPhone && (
            <Grid item xs={12}>
              <InputField name="phone" label="טלפון" />
            </Grid>
          )}
          {config?.isContent && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body">פרטים נוספים</Typography>
                  <Tooltip title={showRichEditor ? 'מצב טקסט' : 'מצב עורך מקצועי'}>
                    <IconButton
                      type="button"
                      shape="square"
                      size="small"
                      color="warning"
                      variant="contained"
                      aria-label={showRichEditor ? 'מצב טקסט' : 'מצב עורך מקצועי'}
                      onClick={() => setShowRichEditor(open => !open)}
                    >
                      <Icon icon="editOutlined" color="inherit" size="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {showRichEditor ? (
                  <Box sx={{ touchAction: 'manipulation', WebkitOverflowScrolling: 'touch' }}>
                    <EditorComponent
                      height={isSmallScreen ? 600 : 500}
                      isMedia={false}
                      setValue={setContent}
                      value={content}
                    />
                  </Box>
                ) : (
                  <Input
                    multiline
                    minRows={6}
                    fullWidth
                    value={
                      !content || content === '<p>טקסט חופשי ותמונה</p>' || content === config?.defaultText
                        ? ''
                        : htmlToPlainText(content)
                    }
                    onChange={event => setContent(plainTextToHtml(event.target.value))}
                    inputProps={{ 'aria-label': 'פרטים נוספים' }}
                  />
                )}
              </Box>
            </Grid>
          )}
          {config?.isImage && (
            <Grid item xs={12}>
              <EventImageUpload initialImageUrl={data.image} initialThumbnailUrl={data.thumbnail} />
            </Grid>
          )}
          {aiFillApplied && (
            <Grid item xs={12}>
              <AiFillConfirmation
                confirmed={aiConfirmed}
                onConfirmedChange={setAiConfirmed}
                saveButtonLabel={isNewEvent ? 'צור מוקד חדש' : 'שמור שינויים'}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Button
              loading={loadingUpdate || loadingAdd || isSaving}
              type="submit"
              fullWidth
              color={isNewEvent ? 'success' : 'secondary'}
              variant="contained"
              disabled={
                formSubmitted ||
                isSaving ||
                (aiFillApplied && !aiConfirmed) ||
                (Boolean(userPermission?.isAdmin && isNewEvent) && !selectedShaliach)
              }
            >
              {isNewEvent ? 'צור מוקד חדש' : 'שמור שינויים '}{' '}
            </Button>
          </Grid>
          {!isNewEvent && (
            <Grid item xs={12}>
              <Button
                onClick={() => setOpenDialog(true)}
                loading={loadingDelete}
                fullWidth
                color="error"
                variant="outlined"
                disabled={loadingDelete}
              >
                מחק מוקד{' '}
              </Button>
            </Grid>
          )}
        </Grid>
      </FormProvider>
    );
  };
  const modeLabel = isNewEvent ? 'יצירת' : 'עריכת';

  const lastEditedCaption =
    !isNewEvent && data?.lastEditedByShaliachId && data?.LastEditor && data?.updatedAt
      ? (() => {
        const ed = data.LastEditor;
        const name =
          [ed.firstName, ed.lastName].filter(Boolean).join(' ').trim() || ed.nickName?.trim() || '';
        if (!name) return null;
        const at = dayjs(data.updatedAt).locale('he');
        const datePart = at.format('DD/MM/YYYY');
        const timePart = at.format('HH:mm');
        return `נערך על ידי ${name} בתאריך ${datePart} בשעה ${timePart}`;
      })()
      : null;
  if (config && isGoogleMapsApiLoaded) {
    return (
      <Box sx={{ mb: 2 }}>
        <Card
          sx={{
            bgcolor: 'background.default',
            boxShadow: 'none',
            position: 'relative'
          }}
        >
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <IconButton
              onClick={() => {
                discardDraft();
                onFormClose?.(index);
              }}
              color="primary"
              shape="circle"
              size="small"
              aria-label="סגור"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <CardContent sx={{ pt: 4 }}>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h4">
                  {`${modeLabel} ${event.data.Operation?.operationName || 'אירוע חדש'}`}
                </Typography>
                {userPermission?.isAdmin && isNewEvent ? (
                  <Box sx={{ mt: 0.5, color: 'secondary.main' }}>
                    <Typography variant="caption">
                    *  מכיוון שאתה מנהל  תוכל לבחור להקים את המוקד עבור שליח אחר 
                    </Typography>
                  </Box>
                ) : null}
                {lastEditedCaption ? (
                  <Box sx={{ mt: 0.5, color: 'error.main' }}>
                    <Typography variant="h6">{lastEditedCaption}</Typography>
                  </Box>
                ) : null}
              </Box>
              {isNewEvent ? (
                <Switch
                  checked={isActive}
                  disabled={loadingUpdate || loadingAdd || loadingDelete}
                  onChange={handleSwitch}
                />
              ) : null}
            </Box>
            <Box sx={{ p: { lg: 3, md: 2, xs: 0 } }}>
              {showAiIntro ? (
                <AiFillIntro
                  isExtracting={isExtracting}
                  onExtractImage={imageUrl => handleImageUploaded(imageUrl, () => setShowAiIntro(false))}
                  onExtractText={text => handleFreeText(text, () => setShowAiIntro(false))}
                  onSkip={() => setShowAiIntro(false)}
                />
              ) : (
                formFields()
              )}
            </Box>
            {!isNewEvent && (
              <DeleteDialog
                openDialog={openDialog}
                setOpenDialog={setOpenDialog}
                handleDeleteEvent={handleDeleteEvent}
              />
            )}
          </CardContent>
        </Card>

        <Dialog
          open={showSaveSuccessDialog}
          onClose={handleSuccessDialogClose}
          maxWidth="sm"
          fullWidth
          primaryAction={{ label: 'אישור', color: 'success', onClick: handleSuccessDialogClose }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
              <CheckCircleIcon color="success" fontSize="large" />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h5">פעולה הושלמה בהצלחה!</Typography>
            </Box>
            <Box sx={{ maxWidth: 300, mx: 'auto' }}>
              <Typography variant="body">{successMessage}</Typography>
            </Box>
          </Box>
        </Dialog>
      </Box>
    );
  } else {
    return <></>;
  }
};

export default EventForm;
